import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { isNull } from 'lodash';
import * as Accordion from '@radix-ui/react-accordion';
import isHotkey from 'is-hotkey';

import {
  folderNameAlreadyExists,
  generateDuplicateName,
  getFoldersByParentId,
} from '@flow/libs/workspace/folders';
import foldersReducer from '@app/reducers/entities/folders';
import { FOLDER_LIMIT } from '@flow/libs/oneflow-client/folders';
import { getAccountFromSessionSelector } from '@app/reducers/session';

import {
  COLLECTION_FOLDER_LIMIT_KEY,
  COLLECTION_DEPTH_LIMIT_KEY,
} from '@flow/libs/account/constants';
import { checkAcl } from '@flow/libs/utils/acl';
import AddFolderIcon from '@app/components/icons/add-folder';
import Button from '@app/components/button';
import Tooltip from '@app/components/tooltip';
import { T } from '@app/components/translation';
import toast from '@app/components/toasts';
import ChevronLeftIcon from '@app/components/icons/chevron-left';
import ChevronDownIcon from '@app/components/icons/chevron-down';
import CircularSpinnerIcon from '@app/components/icons/circular-spinner';
import WorkspaceFolderIcon from '@app/components/icons/workspace-folder';
import { getErrorCode } from '@app/components/api-error';

import { WORKSPACE_ROOT_FOLDER_ID } from '@flow/libs/workspace/folders-constants';
import { FolderSearch } from './folder-search';
import { useFolderFetch } from './use-folder-fetch';
import { useFoldersQuery } from './use-folders-query';
import Branch from './folder-branch';
import style from './folder-tree.module.scss';
import FilterBox from '../filter-box/filter-box';

type FolderTreeType = {
  contractFolderId?: number;
  isModalVersion?: boolean;
  isSaveFolderDestinationModal?: boolean;
  selectedUniqueKey?: any;
  selectedWorkspaceId: number;
  selectedWorkspaceName: string;
  selectedWorkspace: Oneflow.Workspace;
  isReadOnly?: boolean;
  branchContainerClassName?: string;
  onChange: (value: Oneflow.Folder['id']) => void;
};

export const FolderTree = ({
  contractFolderId,
  isModalVersion = false,
  isSaveFolderDestinationModal,
  selectedUniqueKey,
  selectedWorkspaceId,
  selectedWorkspaceName,
  selectedWorkspace,
  isReadOnly = false,
  branchContainerClassName,
  onChange,
}: FolderTreeType) => {
  const dispatch = useDispatch();
  const branchContainerRef = useRef<HTMLDivElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { limits: accountLimits } = useSelector(getAccountFromSessionSelector) as Oneflow.Account;
  const folderLimit =
    accountLimits?.find((each) => each.key === COLLECTION_FOLDER_LIMIT_KEY)?.value ?? FOLDER_LIMIT;
  const folderDepthLimit =
    accountLimits?.find((each) => each.key === COLLECTION_DEPTH_LIMIT_KEY)?.value ?? 2;

  const workspaceAcl = selectedWorkspace.acl;
  const workspaceCreatePermission = checkAcl(workspaceAcl, 'collection:folder:create');
  const root = {
    acl: {
      'folder:sub_folder:create': workspaceCreatePermission ? 'allow' : 'deny',
    } as Oneflow.Folder['acl'],
    id: isNull(selectedUniqueKey) ? WORKSPACE_ROOT_FOLDER_ID : selectedUniqueKey,
  };

  const isWorkspaceRootSelected = root.id === WORKSPACE_ROOT_FOLDER_ID;

  const { data: currentFolder, loading: currentFolderLoading } = useFolderFetch({
    id: root.id,
    enabled: root.id !== WORKSPACE_ROOT_FOLDER_ID,
  });

  const {
    data: folders,
    loading: foldersLoading,
    fetch: fetchFolders,
    loadMore: loadMoreFolders,
  } = useFoldersQuery({
    params: {
      workspaceId: selectedWorkspaceId,
      parentId: root.id !== WORKSPACE_ROOT_FOLDER_ID ? root.id : undefined,
      includeFolderSubtree: 0,
      includeChildCount: 1,
    },
    enabled:
      !currentFolderLoading &&
      (isWorkspaceRootSelected ? true : currentFolder && (currentFolder.childCount as number) > 0),
    queryKey: [
      'folders',
      root.id,
      selectedWorkspaceId,
      isModalVersion,
    ],
  });

  const [isAddMode, setIsAddMode] = useState(false);
  const getSelectedFolderAcl: () => Oneflow.Folder['acl'] = () => {
    if (root.id === WORKSPACE_ROOT_FOLDER_ID) {
      return root.acl;
    }

    return currentFolder?.acl;
  };
  const addFolderPermission = checkAcl(getSelectedFolderAcl(), 'folder:sub_folder:create');

  const addFolder = (name: Oneflow.Folder['name'], parentId: Oneflow.Folder['id']) => {
    dispatch(
      foldersReducer.createFolder({
        data: {
          name,
          workspaceId: selectedWorkspaceId,
          parentId: parentId !== WORKSPACE_ROOT_FOLDER_ID ? parentId : undefined,
        },
        pipe: {
          onSuccess: () => {
            fetchFolders();

            if (isModalVersion) {
              return;
            }

            toast.success({
              id: 'folder-created',
              title: (
                <T
                  _str="The folder was created."
                  _comment="Toast title for the success message when the user has created a folder."
                />
              ),
              duration: 5000,
            });
          },
          onFailure: (error) => {
            const apiErrorMessage = getErrorCode(error);
            if (apiErrorMessage === 'WORKSPACE_FOLDER_LIMIT') {
              if (isModalVersion) {
                setErrorMessage(apiErrorMessage);
                return;
              }

              toast.warning({
                id: 'folder-limit-reached',
                title: (
                  <T
                    _str="Limit of {limit} folders has been reached in this workspace."
                    _comment="Title for the info message when the folder limit has been reached."
                    limit={folderLimit}
                  />
                ),
                duration: 5000,
              });
            }
          },
        },
      }),
    );
  };

  const onFolderAddHandler = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    setIsAddMode(true);
  };

  const onAddFolder = (name: Oneflow.Folder['name'], hasError = false) => {
    if (name !== '' && !hasError) {
      if (!folderNameAlreadyExists(name, root.id, Object.values(folders))) {
        addFolder(name, root.id);
      } else {
        const tempName = generateDuplicateName(name, root.id, Object.values(folders));
        addFolder(tempName, root.id);
      }
    }

    setIsAddMode(false);
  };

  const titleClasses = clsx(style.Title, {
    [style.Small]: isModalVersion,
  });

  const folderIconToolTipMessage = () => {
    const maximumFolderLimit = folders.length === FOLDER_LIMIT;

    if (maximumFolderLimit) {
      return (
        <T
          _str="Your workspace has already reached the maximum of 100 allowed folders."
          _comment="Tooltip for when the add folder button is disabled"
        />
      );
    }

    if (!workspaceCreatePermission) {
      return (
        <T
          _str="You don't have permission to add folders. Contact your admin to set the required permissions."
          _comment="Tooltip for when the add folder button is disabled"
        />
      );
    }

    if (!addFolderPermission) {
      return (
        <T
          _str="Folders support a maximum of {folderDepthLimit} nested levels of subfolders."
          _comment="Tooltip for when the add folder button is disabled"
          folderDepthLimit={folderDepthLimit}
        />
      );
    }

    return (
      <T
        _str="Add folder"
        _comment="Tooltip for add folder button."
      />
    );
  };

  const renderNewFolderButton = () => {
    if (isReadOnly) {
      return null;
    }

    const maximumFolderLimit = folders.length === FOLDER_LIMIT;

    return (
      <Tooltip
        message={folderIconToolTipMessage()}
        side="top"
        theme="oneflow"
        // Modal overlay has z-index 10001
        // so tooltip-z-index=modal-overlay-z-index + 1
        zIndex="10002"
      >
        <div>
          <Button
            customClass={style.AddFolder}
            disabled={!workspaceCreatePermission || !addFolderPermission || maximumFolderLimit}
            onClick={onFolderAddHandler}
            data-testid="add-folder-button"
          >
            <AddFolderIcon height="18px" />
          </Button>
        </div>
      </Tooltip>
    );
  };

  const getName = useCallback(() => {
    if (root.id === WORKSPACE_ROOT_FOLDER_ID) {
      return selectedWorkspaceName || '';
    }

    return currentFolder?.name ?? '';
  }, [
    currentFolder,
    root.id,
    selectedWorkspaceName,
  ]);

  const parentFolder =
    currentFolder?.parentId && currentFolder.resolvedPath
      ? Object.values(currentFolder.resolvedPath).find(
          (folder: any) => folder?.id === currentFolder.parentId,
        )
      : null;

  const handleScroll = useCallback(() => {
    const container = branchContainerRef.current;
    if (!container) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

    if (isNearBottom) {
      loadMoreFolders(FOLDER_LIMIT);
    }
  }, [
    loadMoreFolders,
  ]);

  useEffect(() => {
    const container = branchContainerRef.current;
    if (!container) {
      return;
    }

    container.addEventListener('scroll', handleScroll);

    // Remove the event listener when the component unmounts
    return () => container.removeEventListener('scroll', handleScroll);
  }, [
    handleScroll,
  ]);

  const isEnterOrSpace = (e: React.KeyboardEvent<'button'>) => {
    return isHotkey('enter', e) || isHotkey('space', e);
  };

  const renderFolderTreeContent = () => {
    return (
      <>
        <FolderSearch
          selectedUniqueKey={root.id}
          onChange={onChange}
          selectedWorkspaceId={selectedWorkspaceId}
          selectedWorkspaceName={selectedWorkspaceName}
        />
        <div
          className={clsx(style.BranchContainer, branchContainerClassName, {
            [style.Modal]: isModalVersion,
          })}
          ref={branchContainerRef}
        >
          {root.id !== WORKSPACE_ROOT_FOLDER_ID ? (
            <Accordion.Root
              type="single"
              collapsible
              defaultValue="item-2"
              asChild
            >
              <Accordion.Item
                value="item-2"
                asChild
              >
                <>
                  <Button
                    customClass={style.WorkspaceButton}
                    data-testid="workspace-button"
                    onClick={() => onChange(WORKSPACE_ROOT_FOLDER_ID)}
                    onKeyDown={(e) => {
                      if (isEnterOrSpace(e)) {
                        e.stopPropagation();
                        onChange(WORKSPACE_ROOT_FOLDER_ID);
                      }
                    }}
                  >
                    <span className={style.WorkspaceButtonContentContainer}>
                      <span className={style.WorkspaceButtonContent}>
                        <WorkspaceFolderIcon height="18px" />
                        <p>{selectedWorkspaceName}</p>
                      </span>
                      {!isModalVersion && (
                        <Accordion.Trigger asChild>
                          <ChevronDownIcon
                            height="10px"
                            className={style.AccordionIcon}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          />
                        </Accordion.Trigger>
                      )}
                    </span>
                  </Button>
                  <Accordion.Content>
                    <>
                      <Button
                        icon={<ChevronLeftIcon height="10px" />}
                        customClass={style.BackButton}
                        data-testid="back-button"
                        onClick={() =>
                          onChange(currentFolder?.parentId ?? WORKSPACE_ROOT_FOLDER_ID)
                        }
                        onKeyDown={(e) => {
                          if (isEnterOrSpace(e)) {
                            e.preventDefault();
                            e.stopPropagation();
                            onChange(currentFolder?.parentId ?? WORKSPACE_ROOT_FOLDER_ID);
                          }
                        }}
                      >
                        <p>{parentFolder === null ? selectedWorkspaceName : parentFolder?.name}</p>
                      </Button>
                      <Branch
                        acl={getSelectedFolderAcl()}
                        allFolders={folders}
                        childFolders={getFoldersByParentId(folders, root.id)}
                        contractFolderId={contractFolderId}
                        customClass={style.Root}
                        id={root.id}
                        indentationLevel={root.id === WORKSPACE_ROOT_FOLDER_ID ? -1 : 0}
                        isAddMode={isAddMode}
                        isModalVersion={isModalVersion}
                        name={getName()}
                        onAddFolder={onAddFolder}
                        onFolderAddHandler={onFolderAddHandler}
                        onSelect={onChange}
                        parentId={null}
                        // Need to pass the actual selectedUniqueKey to the branch component instead of root.id
                        // because root.id is defaulted to WORKSPACE_ROOT_FOLDER_ID when selectedUniqueKey is null
                        // if we pass root.id, the workspace root folder will be shown as selected when we are in add mode
                        // showing all documents in the workspace
                        selectedUniqueKey={selectedUniqueKey}
                        isSaveFolderDestinationModal={isSaveFolderDestinationModal}
                        isReadOnly={isReadOnly}
                        loading={foldersLoading}
                      />
                    </>
                  </Accordion.Content>
                </>
              </Accordion.Item>
            </Accordion.Root>
          ) : (
            <Branch
              acl={getSelectedFolderAcl()}
              allFolders={folders}
              childFolders={getFoldersByParentId(folders, root.id)}
              contractFolderId={contractFolderId}
              customClass={style.Root}
              id={root.id}
              indentationLevel={root.id === WORKSPACE_ROOT_FOLDER_ID ? -1 : 0}
              isAddMode={isAddMode}
              isModalVersion={isModalVersion}
              name={getName()}
              onAddFolder={onAddFolder}
              onFolderAddHandler={onFolderAddHandler}
              onSelect={onChange}
              parentId={null}
              // Need to pass the actual selectedUniqueKey to the branch component instead of root.id
              // because root.id is defaulted to WORKSPACE_ROOT_FOLDER_ID when selectedUniqueKey is null
              // if we pass root.id, the workspace root folder will be shown as selected when we are in add mode
              // showing all documents in the workspace
              selectedUniqueKey={selectedUniqueKey}
              isSaveFolderDestinationModal={isSaveFolderDestinationModal}
              isReadOnly={isReadOnly}
              loading={foldersLoading}
            />
          )}
          {foldersLoading && <CircularSpinnerIcon height="10px" />}
        </div>
        {isModalVersion && errorMessage && (
          <p className={style.ErrorMessage}>
            <T
              _str="Limit of {limit} folders has been reached in this workspace."
              _comment="Title for the info message when the folder limit has been reached."
              limit={folderLimit}
            />
          </p>
        )}
      </>
    );
  };

  if (isModalVersion) {
    return (
      <div className={style.Tree}>
        <div className={clsx(style.Header, style.Spacing)}>
          <h3 className={titleClasses}>
            <T
              _str="Folders"
              _comment="Header in the filters section."
            />
          </h3>
          {renderNewFolderButton()}
        </div>
        {renderFolderTreeContent()}
        {isModalVersion && errorMessage && (
          <p className={style.ErrorMessage}>
            <T
              _str="Limit of {limit} folders has been reached in this workspace."
              _comment="Title for the info message when the folder limit has been reached."
              limit={folderLimit}
            />
          </p>
        )}
      </div>
    );
  }

  return (
    <FilterBox
      title={
        <T
          _str="Folders"
          _comment="Header in the filters section."
        />
      }
      workspaceId={selectedWorkspaceId}
      param="folder"
      removeDivider={false}
      headerButton={renderNewFolderButton()}
    >
      {renderFolderTreeContent()}
    </FilterBox>
  );
};
